// src/components/profile/UserPreferences.js
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const UserPreferences = ({ preferences, onSave, onCancel, loading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    theme: preferences?.theme || 'system',
    language: preferences?.language || 'ar',
    email_notifications: preferences?.email_notifications !== undefined ? preferences.email_notifications : true,
    push_notifications: preferences?.push_notifications !== undefined ? preferences.push_notifications : false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="theme" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
          {t('profile.preferences.theme') || 'Theme'}
        </label>
        <select
          id="theme"
          name="theme"
          value={formData.theme}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
        >
          <option value="system">{t('profile.preferences.systemDefault') || 'System Default'}</option>
          <option value="light">{t('profile.preferences.light') || 'Light'}</option>
          <option value="dark">{t('profile.preferences.dark') || 'Dark'}</option>
        </select>
      </div>

      <div>
        <label htmlFor="language" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
          {t('profile.preferences.language') || 'Language'}
        </label>
        <select
          id="language"
          name="language"
          value={formData.language}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
        >
          <option value="ar">{t('profile.preferences.arabic') || 'العربية (Arabic)'}</option>
          <option value="en">{t('profile.preferences.english') || 'English'}</option>
        </select>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <label htmlFor="email_notifications" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-1">
              {t('profile.preferences.emailNotifications') || 'Email Notifications'}
            </label>
            <p className="text-xs text-shayek-light-600 dark:text-shayek-dark-600">
              {t('profile.preferences.emailNotificationsDesc') || 'Receive email notifications about important updates'}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              id="email_notifications"
              name="email_notifications"
              checked={formData.email_notifications}
              onChange={handleChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-shayek-light-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-shayek-primary/20 dark:peer-focus:ring-shayek-primary/30 rounded-full peer dark:bg-shayek-dark-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-shayek-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label htmlFor="push_notifications" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-1">
              {t('profile.preferences.pushNotifications') || 'Push Notifications'}
            </label>
            <p className="text-xs text-shayek-light-600 dark:text-shayek-dark-600">
              {t('profile.preferences.pushNotificationsDesc') || 'Receive browser push notifications (coming soon)'}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              id="push_notifications"
              name="push_notifications"
              checked={formData.push_notifications}
              onChange={handleChange}
              disabled
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-shayek-light-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-shayek-primary/20 dark:peer-focus:ring-shayek-primary/30 rounded-full peer dark:bg-shayek-dark-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-shayek-primary opacity-50 cursor-not-allowed"></div>
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 text-shayek-light-700 dark:text-shayek-dark-700 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('profile.preferences.cancel') || t('common.cancel') || 'Cancel'}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (t('profile.preferences.saving') || 'Saving...') : (t('profile.preferences.save') || 'Save Preferences')}
        </button>
      </div>
    </form>
  );
};

export default UserPreferences;

